/**
 * SIDEBAR NAVIGATION (MULTI LEVEL)
 */

import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";

import {
  Dashboard,
  Settings,
  Analytics,
  Security,
  AccountCircle,
  Logout,
  ChevronLeft,
  LinkedIn,
  Speed,
  ExpandLess,
  ExpandMore,
  Message,
  Tune,
  Add,
  ListAlt,
  PersonAdd,
  Send,
  Mail,
} from "@mui/icons-material";

import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSocket } from "../contexts/SocketContext";

const Sidebar = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { botStatus, isConnected } = useSocket();

  const [expandedMenus, setExpandedMenus] = useState({});
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const computedWidth = open ? 280 : 0; // dynamic drawer width

  /* ------------------ NAVIGATION STRUCTURE ------------------ */
  const navigationItems = [
    { text: "Dashboard", icon: <Dashboard />, path: "/dashboard" },
    {
      text: "Settings",
      icon: <Settings />,
      subItems: [
        {
          text: "General Settings",
          path: "/settings",
          icon: <Tune fontSize="small" />,
        },
        {
          text: "Add Keyword",
          path: "/settings/add-keyword",
          icon: <Add fontSize="small" />,
        },
        {
          text: "Keywords List",
          path: "/settings/keyword-list",
          icon: <ListAlt fontSize="small" />,
        },
      ],
    },
    {
      text: "Follow-up Accounts",
      icon: <PersonAdd />,
      subItems: [
        {
          text: "Like",
          path: "/followup/like",
          icon: <PersonAdd fontSize="small" />,
        },
        {
          text: "Comment",
          path: "/followup/comment",
          icon: <Message fontSize="small" />,
        },
      ],
    },
    {
      text: "Post",
      icon: <Send />,
      subItems: [
        { text: "Draft", path: "/post/draft", icon: <Add fontSize="small" /> },
        {
          text: "Posted",
          path: "/post/posted",
          icon: <ListAlt fontSize="small" />,
        },
      ],
    },
    {
      text: "Message",
      icon: <Message />,
      subItems: [
        {
          text: "List",
          path: "/message/list",
          icon: <ListAlt fontSize="small" />,
        },
        {
          text: "Direct Messages",
          path: "/message/direct",
          icon: <Mail fontSize="small" />,
        },
        {
          text: "Connection Messages",
          path: "/message/connection",
          icon: <PersonAdd fontSize="small" />,
        },
      ],
    },
    { text: "Analytics", icon: <Analytics />, path: "/analytics" },
    { text: "License", icon: <Security />, path: "/license" },
  ];

  /* ------------------ HELPERS ------------------ */
  const handleNavigation = (path) => navigate(path);
  const handleToggleMenu = (text) =>
    setExpandedMenus((prev) => ({ ...prev, [text]: !prev[text] }));
  const handleLogout = () => {
    logout();
    setUserMenuAnchor(null);
  };
  const getBotStatusColor = () => {
    switch (botStatus) {
      case "running":
        return "success";
      case "error":
        return "error";
      default:
        return "default";
    }
  };

  /* ------------------ RECURSIVE MENU RENDER ------------------ */
  const renderMenuItems = (items, level = 0) =>
    items.map((item) => {
      const isExpanded = expandedMenus[item.text];
      return (
        <React.Fragment key={item.text}>
          <ListItem disablePadding sx={{ pl: level * 2 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() =>
                item.subItems
                  ? handleToggleMenu(item.text)
                  : item.path && handleNavigation(item.path)
              }
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              {item.icon && open && <ListItemIcon>{item.icon}</ListItemIcon>}
              {open && <ListItemText primary={item.text} />}
              {item.subItems &&
                open &&
                (isExpanded ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          {item.subItems &&
            isExpanded &&
            renderMenuItems(item.subItems, level + 1)}
        </React.Fragment>
      );
    });

  /* ------------------ SIDEBAR UI ------------------ */
  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: computedWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: computedWidth,
          boxSizing: "border-box",
          transition: "width 0.3s",
          overflowX: "hidden",
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: open ? "space-between" : "center",
          p: 2,
        }}
      >
        {open && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LinkedIn sx={{ color: "primary.main", mr: 1 }} />
            <Typography variant="h6">LinkedIn Bot</Typography>
          </Box>
        )}
        {/* <IconButton onClick={onToggle}>
          <ChevronLeft />
        </IconButton> */}
      </Box>

      <Divider />

      {/* BOT STATUS */}
      {open && (
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1.5,
              bgcolor: "background.default",
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Speed
                sx={{
                  color: isConnected ? "success.main" : "error.main",
                  mr: 1,
                }}
              />
              <Typography variant="body2">
                {isConnected ? "Connected" : "Disconnected"}
              </Typography>
            </Box>
            <Chip label={botStatus} size="small" color={getBotStatusColor()} />
          </Box>
        </Box>
      )}

      <Divider />

      {/* NAVIGATION */}
      <List sx={{ flexGrow: 1, px: 1 }}>
        {renderMenuItems(navigationItems)}
      </List>

      <Divider />

      {/* USER SECTION */}
      {open && (
        <Box sx={{ p: 2 }}>
          <ListItemButton
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            sx={{ borderRadius: 1 }}
          >
            <ListItemIcon>
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.username?.charAt(0)?.toUpperCase() || "U"}
              </Avatar>
            </ListItemIcon>
            <ListItemText primary={user?.username || "User"} />
            <AccountCircle />
          </ListItemButton>

          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
          >
            <MenuItem onClick={() => navigate("/license")}>
              <Security sx={{ mr: 1 }} />
              Manage License
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
